import React, { useState } from 'react';
import { FaLock, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import Swal from 'sweetalert2';
import cpApi from '../../utils/cpApi';
import './CPChangePassword.css';

const CPChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      Swal.fire('Error', 'All fields are required', 'error');
      return;
    }

    if (formData.newPassword.length < 6) {
      Swal.fire('Error', 'New password must be at least 6 characters', 'error');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      Swal.fire('Error', 'New password and confirm password do not match', 'error');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      Swal.fire('Error', 'New password must be different from current password', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await cpApi.put('/controlpanel/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.data.success) {
        Swal.fire({
          title: 'Success!',
          text: 'Password changed successfully',
          icon: 'success',
          confirmButtonColor: '#27ae60'
        });
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cp-change-password-container">
      <div className="cp-change-password-card">
        <div className="cp-change-password-header">
          <FaKey className="cp-change-password-icon" />
          <h2>Change Password</h2>
          <p>Update your Control Panel password</p>
        </div>

        <form onSubmit={handleSubmit} className="cp-change-password-form">
          <div className="cp-change-password-field">
            <label>
              <FaLock /> Current Password
            </label>
            <div className="cp-change-password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                disabled={loading}
              />
              <button
                type="button"
                className="cp-change-password-toggle"
                onClick={() => togglePasswordVisibility('current')}
              >
                {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="cp-change-password-field">
            <label>
              <FaLock /> New Password
            </label>
            <div className="cp-change-password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password (min 6 characters)"
                disabled={loading}
              />
              <button
                type="button"
                className="cp-change-password-toggle"
                onClick={() => togglePasswordVisibility('new')}
              >
                {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="cp-change-password-field">
            <label>
              <FaLock /> Confirm New Password
            </label>
            <div className="cp-change-password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                disabled={loading}
              />
              <button
                type="button"
                className="cp-change-password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
              >
                {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="cp-change-password-submit"
            disabled={loading}
          >
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>

        <div className="cp-change-password-requirements">
          <h4>Password Requirements:</h4>
          <ul>
            <li>Minimum 6 characters</li>
            <li>Must be different from current password</li>
            <li>New password and confirm password must match</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CPChangePassword;
